import { Injectable } from '@angular/core';
import { BillingUserContextService, CurrentUserService } from '@rma/authentication/public/user';
import { SubscribeIntentionEnum, SubscribeIntentionModel, SubscriptionClient } from '@rma/billing-api';
import { NavigationService } from '@rma/generic/util/navigation';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export const DEFAULT_SUBSCRIBE_INTENTION_QUERY_PARAM = 'subscribeIntention';

@Injectable({
  providedIn: 'root',
})
export class SubscribeIntentionService {
  constructor(
    private readonly billingUserContextService: BillingUserContextService,
    private readonly currentUserService: CurrentUserService,
    private readonly subscriptionClient: SubscriptionClient,
    private readonly navigationService: NavigationService,
  ) {}

  public getUserSubscribeIntentions(dateFrom?: Date): Observable<SubscribeIntentionModel[]> {
    if (!dateFrom) {
      dateFrom = new Date();
      dateFrom.setMonth(dateFrom.getMonth() - 1);
    }
    return this.billingUserContextService.userContext$.pipe(
      switchMap((context) =>
        this.subscriptionClient.getSubscribeIntention(dateFrom, context.productUserType, context.productUserGroupingType, context.code),
      ),
    );
  }

  public recordUserSubscribeIntention(intention: SubscribeIntentionEnum): Observable<void> {
    return combineLatest([this.billingUserContextService.userContext$, this.currentUserService.currentUserContext$]).pipe(
      switchMap(([billingContext, userContext]) => {
        if (userContext?.isImpersonating) {
          return of(void 0);
        }
        return this.subscriptionClient.recordSubscribeIntention(
          intention,
          billingContext.productUserType,
          billingContext.productUserGroupingType,
          billingContext.code,
        );
      }),
    );
  }

  public recordUserSubscribeIntentionIfRequired(): Observable<void> {
    const intentionParam = this.navigationService.getQueryParameter(DEFAULT_SUBSCRIBE_INTENTION_QUERY_PARAM);
    if (intentionParam === null || !(intentionParam in SubscribeIntentionEnum)) {
      return of(void 0);
    }

    const intention = intentionParam as SubscribeIntentionEnum;
    return combineLatest([this.billingUserContextService.userContext$, this.currentUserService.currentUserContext$]).pipe(
      switchMap(([billingContext, userContext]) => {
        if (userContext?.isImpersonating) {
          return of(void 0);
        }
        return this.subscriptionClient.recordSubscribeIntention(
          intention,
          billingContext.productUserType,
          billingContext.productUserGroupingType,
          billingContext.code,
        );
      }),
    );
  }
}
